export interface GroupAddress {
  name: string | null
  phone: string | null
  routeId: number | null
  addressLine: string | null
  additionalAddress: string | null
  logoUrl: string | null
  lat: number | null
  lng: number | null
  phoneCodeId: number | null
  socialMediaUrls: string[]
}

function asCoord(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function coordsFrom(record: Record<string, unknown>) {
  const nested =
    record.coordinates && typeof record.coordinates === 'object' && !Array.isArray(record.coordinates)
      ? (record.coordinates as Record<string, unknown>)
      : null

  return {
    lat:
      asCoord(record.lat) ??
      asCoord(record.latitude) ??
      asCoord(nested?.lat) ??
      asCoord(nested?.latitude),
    lng:
      asCoord(record.lng) ??
      asCoord(record.lon) ??
      asCoord(record.longitude) ??
      asCoord(nested?.lng) ??
      asCoord(nested?.lon) ??
      asCoord(nested?.longitude),
  }
}

function asText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  return null
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (typeof item === 'string' && item.trim()) return [item.trim()]
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      const url = asText((item as Record<string, unknown>).url)
      return url ? [url] : []
    }
    return []
  })
}

/** Map `GET .../group/{id}/address` — empty / unknown shapes become `null`. */
export function toGroupAddress(raw: unknown): GroupAddress | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const record = flattenAddress(raw as Record<string, unknown>)
  const nested =
    record.address && typeof record.address === 'object' && !Array.isArray(record.address)
      ? (record.address as Record<string, unknown>)
      : null

  const coords = coordsFrom(record)
  const socialFromItems = asStringArray(record.social_media)
  const socialFromUrls = asStringArray(record.social_media_urls)

  const address: GroupAddress = {
    name: asText(record.name),
    phone: asText(record.phone) ?? asText(record.phone_number),
    routeId: asNumber(record.route_id),
    addressLine:
      asText(record.address) ??
      asText(nested?.full_address) ??
      asText(nested?.address) ??
      asText(record.full_address),
    additionalAddress:
      asText(record.additional_address) ?? asText(record.delivery_note) ?? asText(record.note),
    logoUrl: asText(record.logo_url) ?? asText(record.logo),
    lat: coords.lat,
    lng: coords.lng,
    phoneCodeId: asNumber(record.phone_code_id),
    socialMediaUrls:
      socialFromItems.length > 0
        ? socialFromItems
        : socialFromUrls.length > 0
          ? socialFromUrls
          : [],
  }

  if (!address.name && !address.phone && !address.routeId && !address.addressLine) {
    return null
  }

  return address
}

function flattenAddress(record: Record<string, unknown>): Record<string, unknown> {
  for (const key of ['sender', 'group_address', 'group_merchant', 'data'] as const) {
    const nested = record[key]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...record, ...(nested as Record<string, unknown>) }
    }
  }
  return record
}

/** Jira WEBLP-2222: filled when name, phone, and route/address are present. */
export function hasSenderDetail(address: GroupAddress | null) {
  if (!address) return false
  return Boolean(address.name && address.phone && address.routeId && address.addressLine)
}

export function hasSavedAddressLine(address: GroupAddress | null) {
  return Boolean(address?.addressLine?.trim())
}

export function splitAddressLandmark(address: string) {
  const trimmed = address.trim()
  const comma = trimmed.indexOf(',')
  if (comma <= 0) return { landmark: '', rest: trimmed }
  return {
    landmark: trimmed.slice(0, comma).trim(),
    rest: trimmed.slice(comma),
  }
}
